import React, { useMemo } from 'react';
import { Content } from '@backstage/core-components';
import { Box, Button, Chip, Grid, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ConfirmationNumberIcon from '@material-ui/icons/ConfirmationNumber';
import ReceiptIcon from '@material-ui/icons/Receipt';
import TimerIcon from '@material-ui/icons/Timer';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import CreateDateIcon from '../../../icons/CreateDateIcon';
import UpdatedDateIcon from '../../../icons/UpdatedDateIcon';
import { useTheme } from '@material-ui/core/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  RegistrationRequestItem,
  RegistrationRequestStatus,
  RegistrationRequestStatusEnum,
  SummaryItemConfig,
} from '../../types';
import { useUserDateFormatter } from '../../hooks';
import SummaryRequestView from '../../SummaryRequestView/SummaryRequestView';
import StatusRequest from '../../StatusRequest/StatusRequest';
import {
  ContentWrapper,
  getStatusColor,
  HeaderBar,
  SectionDivider,
  SectionTitle,
  StatusPill,
  StatusText,
} from '../../UserRequestsStyles';
import { DetailsField } from '../../DetailsField';

export function getRegistrationRequestFromState(
  state: unknown,
): RegistrationRequestItem | undefined {
  if (!state) return undefined;
  return state as RegistrationRequestItem;
}

function RegistrationStatusChip({
  status,
}: {
  status: RegistrationRequestStatus;
}) {
  const { palette } = useTheme();

  if (status === RegistrationRequestStatusEnum.NEW)
    return <StatusRequest status="New" />;

  const statusIcon =
    status === RegistrationRequestStatusEnum.DECLINED ? (
      <RemoveCircleIcon
        style={{ color: getStatusColor('declined', palette), fontSize: 20 }}
      />
    ) : (
      <CheckCircleIcon
        style={{ color: getStatusColor('approved', palette), fontSize: 20 }}
      />
    );

  return (
    <StatusPill>
      {statusIcon}
      <StatusText variant="body2">{status.split('_').join(' ')}</StatusText>
    </StatusPill>
  );
}

const RegistrationRequestDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { formatDateTime: formatDate } = useUserDateFormatter();
  const request = getRegistrationRequestFromState(location.state);

  const goBackToRegistrationList = () =>
    navigate('/user-requests/registration');

  const summaryItems = useMemo<SummaryItemConfig[]>(() => {
    if (!request) return [];

    const status = request.status || RegistrationRequestStatusEnum.NEW;
    return [
      {
        icon: <ConfirmationNumberIcon />,
        label: 'Request ID',
        value: request.requestId || request.id,
      },
      {
        icon: <ReceiptIcon />,
        label: 'Ticket Type',
        value: 'Registration Request',
      },
      {
        icon: <TimerIcon />,
        label: 'Status',
        value: <RegistrationStatusChip status={status} />,
      },
      {
        icon: <CreateDateIcon />,
        label: 'Create Date',
        value: formatDate(request.createdDate),
      },
      {
        icon: <UpdatedDateIcon />,
        label: 'Updated On',
        value: formatDate(request.lastModifiedDate),
      },
    ];
  }, [formatDate, request]);

  if (!request) {
    return (
      <Content>
        <HeaderBar>
          <Button
            startIcon={<ArrowBackIcon />}
            color="primary"
            style={{ fontWeight: 700 }}
            onClick={goBackToRegistrationList}
          >
            Back
          </Button>
        </HeaderBar>
        <Typography variant="h6">Registration Request not found</Typography>
      </Content>
    );
  }

  return (
    <ContentWrapper>
      <Content>
        <HeaderBar>
          <Button
            startIcon={<ArrowBackIcon />}
            color="primary"
            style={{ fontWeight: 700 }}
            onClick={goBackToRegistrationList}
          >
            Back
          </Button>
        </HeaderBar>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SectionTitle variant="h2">
              Registration Ticket Information
            </SectionTitle>
            <SummaryRequestView items={summaryItems} columnsPerRow={3} />

            <SectionDivider />

            <SectionTitle variant="h4">Ticket Details</SectionTitle>
            <Box style={{ marginTop: 16 }} />
            <Grid container>
              <Grid item xs={12} md={4}>
                <DetailsField label="Name" value={request.name} />
              </Grid>
              <Grid item xs={12} md={4}>
                <DetailsField
                  label="Company"
                  value={
                    <Chip
                      size="small"
                      color="primary"
                      label={request.companyName ?? '-'}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DetailsField
                  label="Email Address"
                  value={
                    <Chip
                      size="small"
                      color="primary"
                      label={request.email ?? '-'}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DetailsField
                  label="Phone Number"
                  value={request.phoneNumber}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <DetailsField
                  label="Requested Application(s)"
                  value={
                    request.requestedAccessApplication || 'Not specified'
                  }
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <DetailsField
                  label="Intended Use of DXM / Requested Role"
                  value={request.requestedRole || 'Not specified'}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Content>
    </ContentWrapper>
  );
};

export default RegistrationRequestDetails;
